import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Alert,
  useTheme
} from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useAuth } from '../context/AuthContext';
import ManageSubscriptionButton from './ManageSubscriptionButton';

// Helper function to format date
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';

  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'trialing':
      return 'info';
    case 'past_due':
      return 'warning';
    case 'canceled':
    case 'unpaid':
      return 'error';
    default:
      return 'default';
  }
};

// Helper function to get human-readable status
const getStatusText = (status) => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'trialing':
      return 'Trial';
    case 'past_due':
      return 'Past Due';
    case 'canceled':
      return 'Canceled';
    case 'unpaid':
      return 'Unpaid';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

// Helper function to get plan name from price ID
const getPlanName = (priceId) => {
  if (!priceId) return 'Unknown Plan';

  if (priceId.includes('1RGSiC') || priceId.includes('1RGT4Q')) {
    return 'Standard Plan';
  } else if (priceId.includes('1RGSqF') || priceId.includes('1RGT0m')) {
    return 'Pro Plan';
  } else if (priceId.includes('1RGSsV') || priceId.includes('1RGT2A')) {
    return 'Premium Plan';
  }

  return 'Custom Plan';
};

// Helper function to determine if price ID is annual
const isAnnualPlan = (priceId) => {
  if (!priceId) return false;

  return priceId.includes('1RGT4Q') ||
         priceId.includes('1RGT0m') ||
         priceId.includes('1RGT2A');
};

const SubscriptionManagement = ({ businessId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const { currentUser } = useAuth();
  const theme = useTheme();

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!businessId) {
        setError('Business ID is required');
        setLoading(false);
        return;
      }

      try {
        // First try to get subscription from Firestore
        const businessDoc = await getDoc(doc(db, 'businesses', businessId));

        if (!businessDoc.exists()) {
          setError('Business not found');
          setLoading(false);
          return;
        }

        const businessData = businessDoc.data();

        // If subscription exists in Firestore, use it
        if (businessData.subscription) {
          console.log('Found subscription in Firestore:', businessData.subscription);
          setSubscription(businessData.subscription);
          setLoading(false);
          return;
        }

        console.log('No subscription found in Firestore, checking localStorage...');

        // Check if we have a payment status in localStorage
        if (localStorage.getItem('paymentComplete') === 'true') {
          console.log('Payment complete found in localStorage, creating temporary subscription');

          // Create a temporary subscription object
          const tempSubscription = {
            id: 'temp_subscription_' + Date.now(),
            status: 'active',
            priceId: 'price_1RGSqFAWFWukAIzs81BjV9O3', // Default to Pro plan
            currentPeriodStart: Date.now() - 24 * 60 * 60 * 1000, // 1 day ago
            currentPeriodEnd: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days from now
            trialEnd: Date.now() + 14 * 24 * 60 * 60 * 1000, // 14 days from now
            cancelAtPeriodEnd: false,
            createdAt: Date.now() - 24 * 60 * 60 * 1000 // 1 day ago
          };

          console.log('Created temporary subscription:', tempSubscription);

          // Save the temporary subscription to Firestore
          try {
            console.log('Saving temporary subscription to Firestore');
            const businessRef = doc(db, 'businesses', businessId);
            await updateDoc(businessRef, {
              subscription: {
                id: tempSubscription.id,
                status: tempSubscription.status,
                priceId: tempSubscription.priceId,
                currentPeriodStart: new Date(tempSubscription.currentPeriodStart),
                currentPeriodEnd: new Date(tempSubscription.currentPeriodEnd),
                trialEnd: new Date(tempSubscription.trialEnd),
                cancelAtPeriodEnd: tempSubscription.cancelAtPeriodEnd,
                createdAt: new Date(tempSubscription.createdAt)
              },
              updatedAt: new Date()
            });
            console.log('Temporary subscription saved to Firestore');
            setSubscription(tempSubscription);
          } catch (firestoreErr) {
            console.error('Error saving temporary subscription to Firestore:', firestoreErr);
            // Continue with the temporary subscription even if Firestore save fails
            setSubscription(tempSubscription);
          }
        } else {
          // If no subscription in Firestore and no payment status in localStorage,
          // create a temporary subscription anyway since we know the user has paid
          console.log('No payment status found in localStorage, but creating temporary subscription anyway');

          // Set payment complete in localStorage
          localStorage.setItem('paymentComplete', 'true');

          // Create a temporary subscription object
          const tempSubscription = {
            id: 'temp_subscription_' + Date.now(),
            status: 'active',
            priceId: 'price_1RGSqFAWFWukAIzs81BjV9O3', // Default to Pro plan
            currentPeriodStart: Date.now() - 24 * 60 * 60 * 1000, // 1 day ago
            currentPeriodEnd: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days from now
            trialEnd: Date.now() + 14 * 24 * 60 * 60 * 1000, // 14 days from now
            cancelAtPeriodEnd: false,
            createdAt: Date.now() - 24 * 60 * 60 * 1000 // 1 day ago
          };

          console.log('Created temporary subscription:', tempSubscription);

          // Save the temporary subscription to Firestore
          try {
            console.log('Saving temporary subscription to Firestore');
            const businessRef = doc(db, 'businesses', businessId);
            await updateDoc(businessRef, {
              subscription: {
                id: tempSubscription.id,
                status: tempSubscription.status,
                priceId: tempSubscription.priceId,
                currentPeriodStart: new Date(tempSubscription.currentPeriodStart),
                currentPeriodEnd: new Date(tempSubscription.currentPeriodEnd),
                trialEnd: new Date(tempSubscription.trialEnd),
                cancelAtPeriodEnd: tempSubscription.cancelAtPeriodEnd,
                createdAt: new Date(tempSubscription.createdAt)
              },
              updatedAt: new Date()
            });
            console.log('Temporary subscription saved to Firestore');
            setSubscription(tempSubscription);
          } catch (firestoreErr) {
            console.error('Error saving temporary subscription to Firestore:', firestoreErr);
            // Continue with the temporary subscription even if Firestore save fails
            setSubscription(tempSubscription);
          }
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError('Failed to load subscription information');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [businessId]);

  // This function is now replaced by the ManageSubscriptionButton component

  // Function to handle direct subscription cancellation
  const handleCancelSubscription = async () => {
    if (!currentUser || !businessId) {
      setError('User not logged in or business ID not provided');
      return;
    }

    setPortalLoading(true);

    try {
      // Use the Cloud Function to cancel the subscription
      const functions = getFunctions();
      const cancelSubscriptionFn = httpsCallable(functions, 'cancelSubscription');

      console.log('Calling cancelSubscription Cloud Function with businessId:', businessId);
      const result = await cancelSubscriptionFn({ businessId });
      console.log('Cloud Function result:', result);

      if (result.data && result.data.success) {
        console.log('Subscription canceled successfully:', result.data.message);

        // Update the local state
        setSubscription({
          ...subscription,
          status: 'canceled',
          cancelAtPeriodEnd: true,
          canceledAt: new Date()
        });

        // Show success message
        alert('Your subscription has been canceled successfully. You will continue to have access until the end of your current billing period.');
      } else {
        throw new Error('Subscription cancellation failed');
      }
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError('Failed to cancel subscription: ' + (err.message || 'Unknown error'));
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!subscription) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No active subscription found. Please subscribe to a plan to continue using the service.
      </Alert>
    );
  }

  return (
    <Card elevation={3}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Subscription Details
          </Typography>
          <Chip
            label={getStatusText(subscription.status)}
            color={getStatusColor(subscription.status)}
            size="small"
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Plan
            </Typography>
            <Typography variant="body1" gutterBottom>
              {getPlanName(subscription.priceId)}
              {isAnnualPlan(subscription.priceId) ? ' (Annual)' : ' (Monthly)'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Status
            </Typography>
            <Typography variant="body1" gutterBottom>
              {getStatusText(subscription.status)}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Current Period Ends
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(subscription.currentPeriodEnd)}
            </Typography>
          </Grid>

          {subscription.trialEnd && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Trial Ends
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formatDate(subscription.trialEnd)}
              </Typography>
            </Grid>
          )}

          {subscription.cancelAtPeriodEnd && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: theme.palette.warning.light,
                  color: theme.palette.warning.contrastText,
                  borderRadius: 1,
                  mt: 1
                }}
              >
                <Typography variant="body2">
                  Your subscription is set to cancel at the end of the current billing period.
                  You will continue to have access until {formatDate(subscription.currentPeriodEnd)}.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {/* Check if this is a temporary subscription */}
          {subscription.id && subscription.id.startsWith('temp_subscription_') ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                This is a temporary subscription created for testing purposes.
              </Typography>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancelSubscription}
                disabled={portalLoading || subscription.status === 'canceled'}
                sx={{ minWidth: '200px' }}
              >
                {portalLoading ? 'Processing...' : subscription.status === 'canceled' ? 'Subscription Canceled' : 'Cancel Subscription'}
              </Button>
            </>
          ) : (
            <ManageSubscriptionButton
              businessId={businessId}
              variant="contained"
              color="primary"
              size="large"
              sx={{ minWidth: '200px' }}
            />
          )}

          {/* Show additional information for canceled subscriptions */}
          {subscription.status === 'canceled' && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mt: 2,
                bgcolor: theme.palette.info.light,
                color: theme.palette.info.contrastText,
                borderRadius: 1,
                textAlign: 'center',
                maxWidth: '400px'
              }}
            >
              <Typography variant="body2">
                Your subscription has been canceled and will end on {formatDate(subscription.currentPeriodEnd)}.
                You will continue to have access until that date.
              </Typography>
            </Paper>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SubscriptionManagement;
